// angular
import { NgModule, ErrorHandler } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { EffectsModule } from "@ngrx/effects";
import { UpgradeModule } from "@angular/upgrade/static";
// others
import { UserModule } from "@play/user";
import { UserLeagueModule, TournamentListComponent } from "@play/user/league";
import {
    UserTeamsModule,
    TeamListComponent,
    LeaveTeamComponent,
    JoinTeamComponent,
    CreateTeamComponent,
    DeleteTeamComponent,
} from "@play/user/teams";
import { UserLoginModule, LoginComponent } from "@play/user/login";
import { AppEffects } from "./app.effect";
import { reducers as stateReducers, StateModule, skipLoadOnceReducer, RootState, getUser } from "@play/state";
import {
    LeagueWidgetModule,
    ChooseTeamComponent,
    LeaveRosterComponent,
    LeaveLeagueComponent,
    SignupContestantComponent,
    RosterSelectDialogComponent,
    RosterPreselectDialogComponent,
    RosterStatusDialogComponent,
    LeagueWidgetComponent,
    ManageRosterModule,
} from "@play/league-widget";
import { LeagueModule, LeagueInformationComponent, LeagueListComponent, LeagueRankingComponent } from "@play/league";
import { BracketModule } from "@play/cup-bracket";
import { PathViewModule, CupPagePathViewComponent } from "@play/path-view";
import { TournamentApiModule } from "@play/api-client";
import { ContestantSpotlightComponent } from "./ng2/panels/contestant-spotlight/contestant-spotlight.component";
import { ChallengeContestantComponent } from "./ng2/panels/contestant-spotlight/challenge-contestant.component";
import { TranslationModule } from "@play/translate";
import { LeagueWinnerComponent } from "./ng2/panels/league-winner/league-winner.component";
import { LeagueHeaderComponent } from "./ng2/panels/league-header/league-header.component";
import { LeagueWinnerContestantComponent } from "./ng2/panels/league-winner/league-winner-contestant.component";
import {
    CommonsModule,
    CommonsComponentModule,
    CommonsLocationModule,
    CommonsRestrictionsModule,
    ExpansionPanelComponent,
    PlayHeaderComponent,
    AppBannerComponent,
    PrivacyPolicyPopupComponent,
    FormatDateComponent,
    TrustedBadgeComponent,
    BaseConfig,
    BASE_CONFIG,
} from "@play/common";
import { FFArankingModule, FfaRankingComponent, RankingFfaPickerComponent } from "@play/ranking/ffa";
import { FFAMatchModule, FfaMatchComponent } from "@play/match/ffa";
import { MatchModule, EseaMatchButtonComponent } from "@play/match";
import { MatchMediaUploadComponent, MatchMediaModule } from "@play/match/media";
import { AdminUiModule, FFAResultContainerComponent } from "@play/admin-ui";
import { EslIdAuthService } from "./ng1/eslid/EslIdAuthService";
import { eslAPIInterceptorProviders } from "./app.interceptor";
import { AppComponent } from "./app.component";
import { CupPageCupBracketComponent } from "@play/cup-bracket";
import { ErrorHandlingModule, ErrorReporter, breadcrumbsReducer, RAYGUN_API_KEY, APPLICATION_VERSION } from "@play/error-reporting";
import { LocationNg1Service } from "./ng2/selected-participant/location.service";
import { LocationService } from "@play/cup-page";
import { eventServiceProvider, dialogServiceProvider, dialogStringServiceProvider, locationProvider } from "./ng1/upgrade";
import { apiUrlProvider, errorHandlerFactory } from "../environments/configProviders";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { PremiumModule, PremiumButtonComponent, PremiumOfferComponent, PremiumBadgeComponent } from "@play/premium";
import { ShopModule, ShopCodeButtonComponent } from "@play/shop";
import { RegionModule, RegionSuggestionComponent } from "@play/region";
import { CaptchaModule, CaptchaComponent } from "@play/captcha";
import * as pkg from "../../package.json";
import { play } from "./ng1/Application";
import { CommonModule } from "@angular/common";

declare var Drupal: any;

export function baseConfigFactory(): BaseConfig {
    return {
        timezone: Drupal.settings.user.timezone.name,
        navnode: Drupal.settings.legacy_content.prefix,
    };
}

@NgModule({
    imports: [
        CommonModule,
        BrowserModule,
        UpgradeModule,
        StoreModule.forRoot(stateReducers, {
            metaReducers: [skipLoadOnceReducer, breadcrumbsReducer],
        }),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: false,
        }),
        EffectsModule.forRoot([AppEffects]),
        LeagueWidgetModule,
        ManageRosterModule,
        FFArankingModule,
        FFAMatchModule,
        MatchMediaModule,
        MatchModule,
        TranslationModule,
        CommonsModule,
        CommonsComponentModule,
        CommonsLocationModule,
        CommonsRestrictionsModule,
        AdminUiModule,
        StateModule,
        TournamentApiModule,
        BracketModule,
        PathViewModule,
        ErrorHandlingModule,
        BrowserAnimationsModule,
        UserModule,
        UserLeagueModule,
        UserTeamsModule,
        UserLoginModule,
        PremiumModule,
        ShopModule,
        RegionModule,
        CaptchaModule,
        LeagueModule,
    ],
    declarations: [
        AppComponent,
        ContestantSpotlightComponent,
        ChallengeContestantComponent,
        LeagueWinnerComponent,
        LeagueHeaderComponent,
        LeagueWinnerContestantComponent,
    ],
    providers: [
        EslIdAuthService,
        eventServiceProvider,
        dialogServiceProvider,
        dialogStringServiceProvider,
        locationProvider,
        eslAPIInterceptorProviders,
        apiUrlProvider,
        {
            provide: LocationService,
            useClass: LocationNg1Service,
        },
        {
            provide: ErrorHandler,
            useFactory: errorHandlerFactory,
            deps: [ErrorReporter],
        },
        {
            provide: RAYGUN_API_KEY,
            useValue: "QY2rMHt2ryL75q8uZjT0Ww==",
        },
        {
            provide: APPLICATION_VERSION,
            useValue: pkg.version,
        },
        {
            provide: BASE_CONFIG,
            useFactory: baseConfigFactory,
            deps: [],
        },
    ],
    entryComponents: [
        AppComponent,
        LeagueWidgetComponent,
        RosterStatusDialogComponent,
        RosterPreselectDialogComponent,
        RosterSelectDialogComponent,
        SignupContestantComponent,
        LeaveLeagueComponent,
        LeaveRosterComponent,
        ChooseTeamComponent,
        ContestantSpotlightComponent,
        LeagueWinnerContestantComponent,
        LeagueHeaderComponent,
        FfaRankingComponent,
        FfaMatchComponent,
        EseaMatchButtonComponent,
        RankingFfaPickerComponent,
        ExpansionPanelComponent,
        CaptchaComponent,
        MatchMediaUploadComponent,
        FFAResultContainerComponent,
        CupPageCupBracketComponent,
        CupPagePathViewComponent,
        TournamentListComponent,
        TeamListComponent,
        LoginComponent,
        JoinTeamComponent,
        CreateTeamComponent,
        DeleteTeamComponent,
        LeaveTeamComponent,
        PlayHeaderComponent,
        PremiumButtonComponent,
        PremiumOfferComponent,
        ShopCodeButtonComponent,
        RegionSuggestionComponent,
        LeagueInformationComponent,
        FormatDateComponent,
        LeagueListComponent,
        LeagueRankingComponent,
        PremiumBadgeComponent,
        TrustedBadgeComponent,
        AppBannerComponent,
        PrivacyPolicyPopupComponent,
    ],
})
export class AppModule {
    constructor(private upgrade: UpgradeModule) {}

    public ngDoBootstrap(): void {
        this.upgrade.bootstrap(document.body, [play.name], { strictDi: true });
    }
}
